import { create } from 'zustand';

const STORAGE_KEY = 'flusia_active_role';
const IMPERSONATE_FLAG = 'flusia_impersonate_mode';

export type ActiveRole = 'crm' | 'admin';

interface UserProfileForRole {
  organization_id?: string | null;
  is_super_admin?: boolean;
  is_partner_admin?: boolean;
}

interface ActiveRoleStore {
  activeRole: ActiveRole | null;
  setActiveRole: (role: ActiveRole) => void;
  clearActiveRole: () => void;
}

/**
 * Verifica se un utente ha accesso sia al CRM (ha organization_id)
 * sia all'area admin (is_super_admin o is_partner_admin).
 * Usa SOLO dati dal profilo DB, MAI da localStorage.
 */
export const hasDualRole = (profile: UserProfileForRole | null | undefined): boolean => {
  if (!profile) return false;
  const hasOrg = !!profile.organization_id;
  const hasAdmin = !!profile.is_super_admin || !!profile.is_partner_admin;
  return hasOrg && hasAdmin;
};

// Tab-aware storage: in modalità impersonate la tab usa sessionStorage (scope per tab,
// nessun leak da/verso la tab admin). In modalità normale, localStorage (persiste tra
// refresh e tab della stessa sessione admin).
function isImpersonateTab(): boolean {
  if (typeof window === 'undefined') return false;
  return window.sessionStorage.getItem(IMPERSONATE_FLAG) === '1';
}

function getStorage(): Storage | null {
  if (typeof window === 'undefined') return null;
  return isImpersonateTab() ? window.sessionStorage : window.localStorage;
}

export const useActiveRoleStore = create<ActiveRoleStore>((set) => {
  const storage = getStorage();
  // In tab impersonate forziamo SEMPRE 'crm': l'utente impersonato non è admin
  // (eventuale superadmin target è già bloccato lato edge function).
  let initialRole: ActiveRole | null = null;
  if (storage && !isImpersonateTab()) {
    const stored = storage.getItem(STORAGE_KEY);
    if (stored === 'crm' || stored === 'admin') initialRole = stored;
  } else if (isImpersonateTab()) {
    initialRole = 'crm';
  }

  return {
    activeRole: initialRole,

    setActiveRole: (role: ActiveRole) => {
      const s = getStorage();
      if (s) s.setItem(STORAGE_KEY, role);
      set({ activeRole: role });
    },

    clearActiveRole: () => {
      const s = getStorage();
      if (s) s.removeItem(STORAGE_KEY);
      set({ activeRole: null });
    },
  };
});
